import { URL_PARAMETER_DOCUMENT } from '../../../constants/url.parameter.names';
import { initiateTextFileDownload } from '../../../utility/download.helpers';
import { xmlToStringMulti } from '../../../utility/xmlHelpers';
import { xmlToString } from '../../../xmlUtility';
import {
    paramDownload,
    paramDsName,
    paramFileName,
    paramInner,
    paramOuter,
    paramXPath,
} from '../constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeXPath,
} from '../constants/parameterTypes';
import { failure } from '../helpers/commandResult';
import {
    getBoolParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import { getValidNode } from '../helpers/qRules.node.helpers';

const DEFAULT_FILE_NAME = 'document.xml';

const INNER_OUTER_PRECEDENCE_MESSAGE = `If both /${paramInner} and /${paramOuter} are true, /${paramOuter} takes precedence.`;

const optionalParameters = {
    [paramDsName]: {
        description:
            'Name of the data source from which to retrieve XML. Defaults to main data source if omitted.',
        type: typeDataSource,
    },
    [paramXPath]: {
        description:
            'XPath to the node to get. If omitted, defaults to the root of the data source. If multiple nodes are found, gets the XML of the first node found.',
        type: typeXPath,
        dsParentName: paramDsName,
    },
    [paramInner]: {
        description: `(boolean, defaults to false) Get the inner XML of the node (i.e. don't include the node itself). ${INNER_OUTER_PRECEDENCE_MESSAGE}`,
        type: typeBoolean,
    },
    [paramOuter]: {
        description: `(boolean, defaults to true) Get the XML of node (i.e. incldue the node itself). ${INNER_OUTER_PRECEDENCE_MESSAGE}`,
        type: typeBoolean,
    },
    [paramDownload]: {
        description: '(boolean, defaults to false) Download the XML as a file.',
        type: typeBoolean,
    },
    [paramFileName]: {
        description: `File name to use when downloading the XML. If unspecified, file name is determined as follows:\n\n- If /${paramDsName} is specified, that name plus the extension .xml\n- Otherwise, if the current document has a file name, (e.g. a ${URL_PARAMETER_DOCUMENT}= parameter is present in the form URL), that file name\n- Otherwise, ${DEFAULT_FILE_NAME}`,
    },
};

const errorFailedToSelectSource = 'Failed to select source field.';
const errorFailedToDetermineValue = 'Failed to determine value.';
const description =
    "Returns the full inner or outer XML of a node in one of the form's data sources.";

const getXml = (target, outer, inner) => {
    if (!target) {
        return failure(errorFailedToSelectSource);
    }

    // Default is outer, and outer if both are true
    const useOuter = outer || !inner;

    if (useOuter) {
        return xmlToString(target.getNode());
    }

    const childNodes = target
        .childNodes()
        .map((node) => node?.getNode())
        .filter(Boolean);

    return xmlToStringMulti(childNodes, '');
};

export const determineFileName = (fileName, environment, dsName) => {
    if (fileName) {
        return fileName;
    }

    if (dsName) {
        return `${dsName}.xml`;
    }

    const currentFileName = environment.getCurrentFileName();

    if (currentFileName) {
        return currentFileName;
    }

    return DEFAULT_FILE_NAME;
};

const download = (specifiedFileName, environment, dsName, xml) => {
    const fileNameToUse = determineFileName(
        specifiedFileName,
        environment,
        dsName,
    );

    const success = initiateTextFileDownload(xml, fileNameToUse);

    if (!success) {
        throw new Error('Failed to initiate file download.');
    }
};

const execute = ({ dataSources, environment, parameters }) => {
    const dsName = getParamValue(parameters, paramDsName);
    const xPath = getParamValue(parameters, paramXPath, '/');
    const inner = getBoolParamValue(parameters, paramInner);
    const outer = getBoolParamValue(parameters, paramOuter);
    const dataSource = getDataSource(dataSources, dsName);
    const target = getValidNode(dataSource, xPath, errorFailedToSelectSource);
    const downloadXml = getBoolParamValue(parameters, paramDownload);
    const fileName = getParamValue(parameters, paramFileName);

    try {
        const result = getXml(target, outer, inner);

        if (downloadXml) {
            download(fileName, environment, dsName, result);
        }

        return result;
    } catch (e) {
        console.error(e);
        throw new Error(errorFailedToDetermineValue);
    }
};

const GetXml = {
    description,
    optionalParameters,
    execute,
};

export default GetXml;
